import React, { useState } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import {
  AppBar, Toolbar, Typography, IconButton, Drawer, List, ListItem,
  ListItemButton, ListItemIcon, ListItemText, Box, Avatar, Divider,
  BottomNavigation, BottomNavigationAction, Paper, useMediaQuery, useTheme
} from '@mui/material';
import {
  Menu as MenuIcon, CalendarMonth, School, People, FactCheck,
  Payment, Assessment, Logout, Close, EventNote
} from '@mui/icons-material';
import { useAuth } from '../AuthContext';

const navItems = [
  { label: 'Calendar', icon: <CalendarMonth />, path: '/' },
  { label: 'Classes', icon: <School />, path: '/classes' },
  { label: 'Students', icon: <People />, path: '/students' },
  { label: 'Attendance', icon: <FactCheck />, path: '/attendance' },
  { label: 'Payments', icon: <Payment />, path: '/payments' },
  { label: 'Reports', icon: <Assessment />, path: '/reports' },
  { label: 'Schedule', icon: <EventNote />, path: '/schedule' },
];

const bottomNavItems = [
  { label: 'Calendar', icon: <CalendarMonth />, path: '/' },
  { label: 'Students', icon: <People />, path: '/students' },
  { label: 'Attendance', icon: <FactCheck />, path: '/attendance' },
  { label: 'Payments', icon: <Payment />, path: '/payments' },
  { label: 'Reports', icon: <Assessment />, path: '/reports' },
];

export default function Layout() {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const { logout, user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const currentBottomNav = bottomNavItems.findIndex(item => {
    if (item.path === '/') return location.pathname === '/';
    return location.pathname.startsWith(item.path);
  });

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      <AppBar position="sticky" elevation={0} sx={{ 
        bgcolor: 'white', color: 'text.primary',
        borderBottom: '1px solid', borderColor: 'divider'
      }}>
        <Toolbar>
          <IconButton edge="start" onClick={() => setDrawerOpen(true)} sx={{ mr: 1 }}>
            <MenuIcon />
          </IconButton>
          <Typography variant="h6" sx={{ 
            flexGrow: 1, 
            background: 'linear-gradient(135deg, #6366f1, #ec4899)',
            WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
            fontWeight: 700
          }}>
            Class Monitor
          </Typography>
          <Avatar
            src={user?.photo || undefined}
            sx={{ bgcolor: 'primary.main', width: 32, height: 32, fontSize: 14 }}
          >
            {(user?.name || 'A')[0].toUpperCase()}
          </Avatar>
        </Toolbar>
      </AppBar>

      <Drawer anchor="left" open={drawerOpen} onClose={() => setDrawerOpen(false)}>
        <Box sx={{ width: 280, pt: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 2, mb: 1 }}>
            <Typography variant="h6" sx={{ fontWeight: 700, color: 'primary.main' }}>
              Class Monitor
            </Typography>
            <IconButton onClick={() => setDrawerOpen(false)}><Close /></IconButton>
          </Box>
          <Divider sx={{ mb: 1 }} />
          <List>
            {navItems.map(item => (
              <ListItem key={item.path} disablePadding>
                <ListItemButton
                  selected={item.path === '/' ? location.pathname === '/' : location.pathname.startsWith(item.path)}
                  onClick={() => { navigate(item.path); setDrawerOpen(false); }}
                  sx={{ mx: 1, borderRadius: 2, mb: 0.5 }}
                >
                  <ListItemIcon sx={{ minWidth: 40 }}>{item.icon}</ListItemIcon>
                  <ListItemText primary={item.label} />
                </ListItemButton>
              </ListItem>
            ))}
          </List>
          <Divider sx={{ my: 1 }} />
          <List>
            <ListItem disablePadding>
              <ListItemButton onClick={logout} sx={{ mx: 1, borderRadius: 2 }}>
                <ListItemIcon sx={{ minWidth: 40 }}><Logout /></ListItemIcon>
                <ListItemText primary="Logout" />
              </ListItemButton>
            </ListItem>
          </List>
        </Box>
      </Drawer>

      <Box sx={{ flex: 1, pb: isMobile ? 8 : 2 }}>
        <Outlet />
      </Box>

      {isMobile && (
        <Paper sx={{ position: 'fixed', bottom: 0, left: 0, right: 0, zIndex: 1200 }} elevation={8}>
          <BottomNavigation
            value={currentBottomNav}
            onChange={(_, newValue) => navigate(bottomNavItems[newValue].path)}
            showLabels
            sx={{ height: 64 }}
          >
            {bottomNavItems.map(item => (
              <BottomNavigationAction key={item.path} label={item.label} icon={item.icon} />
            ))}
          </BottomNavigation>
        </Paper>
      )}
    </Box>
  );
}
